import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { fetchPublicEvents } from "@/lib/api";
import SectionHeader from "@/components/SectionHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import { Badge } from "@/components/ui/badge";
import { CalendarDays, MapPin, Lock, Clock } from "lucide-react";

const mapsUrl = (loc) =>
    `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(loc)}`;

function sameDay(a, b) {
    return a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

export default function EventsPage() {
    const [events, setEvents] = useState([]);
    const [selected, setSelected] = useState(undefined);

    useEffect(() => {
        fetchPublicEvents().then(setEvents).catch(() => setEvents([]));
    }, []);

    const eventDates = useMemo(
        () => events.map((e) => ({
            date: new Date(e.start_date + "T00:00:00"),
            visibility: e.visibility,
        })),
        [events]
    );

    const upcoming = useMemo(() => {
        const today = new Date(); today.setHours(0,0,0,0);
        return events
            .filter((e) => new Date(e.start_date + "T00:00:00") >= today)
            .sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
    }, [events]);

    const dayEvents = useMemo(() => {
        if (!selected) return [];
        return events.filter((e) => sameDay(new Date(e.start_date + "T00:00:00"), selected));
    }, [events, selected]);

    const modifiers = {
        publicEvent: eventDates.filter((d) => d.visibility === "public").map((d) => d.date),
        privateEvent: eventDates.filter((d) => d.visibility === "private").map((d) => d.date),
    };

    const modifiersClassNames = {
        publicEvent: "day-has-event",
        privateEvent: "day-has-event day-private",
    };

    return (
        <div data-testid="events-page">
            <section className="section">
                <div className="container-narrow">
                    <SectionHeader
                        eyebrow="On The Road"
                        title="Find Mo at the next pop-up."
                        subtitle="Public events show name + location. Private bookings appear as 'Private Event' — we keep your party between us."
                    />
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                        {/* Calendar */}
                        <div className="lg:col-span-7">
                            <Card className="surface-card border-0" data-testid="events-calendar-card">
                                <CardContent className="p-4 sm:p-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="flex items-center gap-2 text-white/80">
                                            <CalendarDays size={18} className="text-orange-300" />
                                            <span className="font-display tracking-[0.06em]">Upcoming Calendar</span>
                                        </div>
                                        <div className="flex items-center gap-3 text-xs text-white/60">
                                            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-gradient-to-b from-orange-400 to-red-500 inline-block" /> Public</span>
                                            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-white/60 inline-block" /> Private</span>
                                        </div>
                                    </div>
                                    <Calendar
                                        mode="single"
                                        selected={selected}
                                        onSelect={setSelected}
                                        modifiers={modifiers}
                                        modifiersClassNames={modifiersClassNames}
                                        className="rounded-md"
                                        data-testid="events-calendar"
                                    />
                                </CardContent>
                            </Card>

                            {/* Selected day list */}
                            {selected && (
                                <Card className="surface-card border-0 mt-4" data-testid="events-selected-day-card">
                                    <CardContent className="p-5">
                                        <div className="text-[11px] uppercase tracking-[0.32em] text-orange-300/90">
                                            {selected.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
                                        </div>
                                        {dayEvents.length === 0 ? (
                                            <p className="text-white/70 mt-2">No events on this day.</p>
                                        ) : (
                                            <div className="mt-3 space-y-3">
                                                {dayEvents.map((e) => (
                                                    <div key={e.id} className="flex items-start justify-between gap-3 p-3 rounded-md border border-white/10 bg-black/30" data-testid="events-selected-day-item">
                                                        <div>
                                                            <div className="font-display text-lg text-white flex items-center gap-2">
                                                                {e.title}
                                                                {e.visibility === "private" && <Lock size={14} className="text-white/60" />}
                                                            </div>
                                                            {e.location && (
                                                                <a href={mapsUrl(e.location)} target="_blank" rel="noopener noreferrer" className="text-white/70 text-sm flex items-center gap-1 mt-1 hover:text-orange-300 transition-colors">
                                                                    <MapPin size={14} className="text-orange-300 shrink-0" /> {e.location}
                                                                </a>
                                                            )}
                                                            {e.start_time && (
                                                                <div className="text-white/60 text-sm flex items-center gap-1 mt-0.5"><Clock size={14} /> {e.start_time}{e.end_time ? ` – ${e.end_time}` : ''}</div>
                                                            )}
                                                        </div>
                                                        <Badge variant="outline" className={e.visibility === "public" ? "border-orange-400/40 text-orange-300 bg-orange-400/10" : "border-white/20 text-white/70 bg-white/5"}>{e.visibility}</Badge>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                            )}
                        </div>

                        {/* Right rail */}
                        <div className="lg:col-span-5 space-y-6">
                            <Card className="surface-card border-0" data-testid="events-upcoming-card">
                                <CardContent className="p-5">
                                    <div className="font-display text-xl tracking-[0.05em] mb-3">Upcoming Events</div>
                                    {upcoming.length === 0 ? (
                                        <p className="text-white/70">No upcoming events listed yet.</p>
                                    ) : (
                                        <ul className="space-y-3" data-testid="events-upcoming-list">
                                            {upcoming.slice(0, 8).map((e) => (
                                                <motion.li
                                                    key={e.id}
                                                    initial={{ opacity: 0, y: 6 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ duration: 0.4 }}
                                                    className="p-3 rounded-md border border-white/10 bg-black/30"
                                                    data-testid="events-upcoming-item"
                                                >
                                                    <div className="text-[10px] uppercase tracking-[0.32em] text-orange-300/90">
                                                        {new Date(e.start_date + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                                                    </div>
                                                    <div className="font-display text-lg text-white flex items-center gap-2">
                                                        {e.title}
                                                        {e.visibility === "private" && <Lock size={14} className="text-white/60" />}
                                                    </div>
                                                    {e.location && (
                                                        <a href={mapsUrl(e.location)} target="_blank" rel="noopener noreferrer" className="text-white/70 text-sm flex items-center gap-1 mt-1 hover:text-orange-300 transition-colors">
                                                            <MapPin size={14} className="text-orange-300 shrink-0" /> {e.location}
                                                        </a>
                                                    )}
                                                    {e.start_time && (
                                                        <div className="text-white/60 text-sm mt-0.5">{e.start_time}{e.end_time ? ` – ${e.end_time}` : ''}</div>
                                                    )}
                                                </motion.li>
                                            ))}
                                        </ul>
                                    )}
                                </CardContent>
                            </Card>

                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
